import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Observable, ReplaySubject, Subscription, filter, first, switchMap } from "rxjs";
import { ConfigService } from "../config/config.service";
import { ConsentService } from "../consent/consent.service";

@Injectable({
  providedIn: "root",
})
export class PublicidadService {
  private API_URL = "https://www.ispot.es/ws/";
  private _property$: ReplaySubject<any> = new ReplaySubject(1);
  private rSub: Subscription;
  private renderer: Renderer2;
  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private consentService: ConsentService,
    private rendererFactory: RendererFactory2,
    private configService: ConfigService,
  ) {
    const constantesGlobales: any = this.configService.getConstantesGlobales();
    const idIspot = constantesGlobales.idPublicidad;
    this.renderer = this.rendererFactory.createRenderer(null, null);
    if (consentService.publicidadInitSubject) {
      this.consentService.publicidadInitSubject.pipe(first()).subscribe(() => {
        if (this.router?.events) {
          this.rSub = this.router.events
            .pipe(
              filter((event) => event instanceof NavigationEnd),
              switchMap(() => this.fetchPublicidadIspot(idIspot)),
            )
            .subscribe((result) => {
              this._property$.next(result);
            });
        }

        const publiIspot = this.fetchPublicidadIspot(idIspot);
        if (publiIspot) {
          publiIspot.pipe(first()).subscribe((result) => {
            this._property$.next(result);
          });
        }
      });
    }
  }

  private fetchPublicidadIspot(pid): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = new HttpParams().set("pid", pid);
    return this.httpClient.get(this.API_URL + "getwebdirectpublibyget", { headers: headers, params: body });
  }

  public getPubli(): Observable<any> {
    return this._property$.asObservable();
  }

  public setPubli(value) {
    this._property$.next(value);
  }

  /**
   * La función `insertHtmlPubliContainers` crea y formatea elementos HTML de contenedores de
   * publicidad en un fragmento de código html.
   * @returns La función `insertHtmlPubliContainers()` devuelve un objeto `SafeHtml` después de
   * procesar el contenido HTML.
   */
  insertHtmlPubliContainers(html: string): string {
    const tempDiv = this.renderer.createElement("div");
    const formattedString = this.formatHtmlText(html);
    this.renderer.setProperty(tempDiv, "innerHTML", formattedString);
    const updatedHtml = this.calcPubliPositions(tempDiv);
    return updatedHtml;
  }

  /**
   * `formatHtmlText` da formato al texto HTML reemplazando los saltos de línea con etiquetas de párrafo
   * y asegurándose de que el texto esté envuelto al principio y final en etiquetas de párrafo.
   * @param {string} htmlText - Toma una cadena de texto HTML como entrada y la formatea.
   * @returns {string} Devuelve una cadena formateada de HTML.
   */
  formatHtmlText(htmlText: string): string {
    if (!htmlText || typeof htmlText !== "string" || typeof htmlText === "undefined") {
      return "";
    }

    htmlText = htmlText?.replace(/<div>\s*<\/div>/gi, "");
    htmlText = htmlText?.replace(/<\/div>\s*<div>/gi, "</p><p>");
    htmlText = htmlText?.replace(/<div>/gi, "<p>");
    htmlText = htmlText?.replace(/<\/div>/gi, "</p>");

    if (!htmlText.includes("<p>")) {
      return htmlText;
    }
    const startIndex = htmlText.indexOf("<p");
    let formattedString = htmlText.substring(startIndex, htmlText.length);

    if (formattedString.startsWith("<p>") && formattedString.endsWith("</p>")) {
      return formattedString;
    }
    if (!formattedString.startsWith("<p>")) {
      formattedString = `<p>${formattedString}`;
    }
    if (!formattedString.endsWith("</p>")) {
      formattedString = `${formattedString}</p>`;
    }
    return formattedString.replace(/<p>\s*<\/p>/gi, "");
  }

  /**
   * Calcula las posiciones para insertar contenedores de publicidad dentro de un array de párrafos HTML
   *  determinado en función del recuento de caracteres de los párrafos.
   * @param pArray - Toma un parámetro `pArray`, que se espera que sea un array de párrafos. Luego,
   *  la función realiza algunos cálculos basados en el contenido de `<p> `elementos dentro de este
   *  contenedor para determinar dónde insertar ciertos elementos.
   * @returns La función `calcPubliPositions` devuelve el contenido HTML interno del elemento
   * `tempDiv` después de procesar los párrafos.
   */
  calcPubliPositions(tempDiv): string {
    let charCount: number = 0;
    let divCount: number = 1;
    const paragraphs = Array.from(tempDiv.querySelectorAll("p"));
    paragraphs.forEach((p: any) => {
      charCount += p.innerHTML.length;

      if (divCount === 1 && charCount > 750) {
        // this.insertAfterP(p, `publi-noticia-${divCount}`, 'publi-container');
        this.insertAfterP(p, `ROBA_${divCount}_MOBILE`, "publi-container");
      } else {
        /*   else if (charCount > 1500) {
        if (divCount === 1) {
          // this.insertAfterP(p, `publi-noticia-${divCount}`, 'publi-container');
          this.insertAfterP(p, `ROBA_${divCount}_MOBILE`, 'publi-container');
        }
      } */
        return;
      }
      charCount = 0;
      divCount++;
    });
    return tempDiv.innerHTML;
  }

  /**
   * La función inserta un div después de un elemento parrafo que se introduce como argumento.
   * @param p - El parámetro `p` en la función es una referencia a un elemento de
   * párrafo en el documento HTML.
   */
  insertAfterP(p, divName: string, parentClass: string) {
    const container = this.renderer.createElement("div");
    this.renderer.addClass(container, parentClass);
    const newDiv = this.renderer.createElement("div");
    this.renderer.setAttribute(newDiv, "id", divName);
    this.renderer.addClass(newDiv, divName);
    this.renderer.appendChild(container, newDiv);
    if (p.nextSibling) {
      this.renderer.insertBefore(p.parentNode, container, p.nextSibling);
    } else {
      this.renderer.appendChild(p.parentNode, container);
    }
  }
}
